import { Formik, useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import { Fragment, useEffect, useState } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Button } from '@hivebrite/design-system/Button';

import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import ModalWithTemplate from 'hb-react/shared/components/DesignSystem/Modal/ModalWithTemplate';
import ErrorFocus from 'hb-react/shared/components/formik/components/ErrorFocus';
import { Number as NumberInput } from 'hb-react/shared/components/formik/components/inputs';
import { numberWithCurrency } from 'hb-react/shared/utils/formatNumber';
import { validateMinimum } from 'hb-react/shared/utils/validators/minimum';

const hasDonation = ({ donations = [] }) =>
  donations
    .map(({ amount }) => Number(amount))
    .reduce((donation, total) => donation + total, 0);

const Footer = ({ onClose }) => {
  const { values, isSubmitting, isValid, submitForm } = useFormikContext();

  const donated = hasDonation(values);

  const buttonText = donated ? __('Yes, submit donation') : __('No, thanks');

  return (
    <Box justifyContent="flex-end" mt={6}>
      <Button onClick={onClose} variant="secondary" mr={2}>
        {__('Go back')}
      </Button>
      <Button
        onClick={() => submitForm()}
        isDisabled={!isValid}
        isLoading={isSubmitting}
      >
        {buttonText}
      </Button>
    </Box>
  );
};

const Header = ({ header }) =>
  __(header || 'Would you like to add a donation onto your order?');

const Donation = ({
  currentUser,
  name,
  title,
  description,
  minimum_amount: minimumAmount,
  currency,
}) => {
  const placeholder =
    minimumAmount > 0 && currency
      ? `${__('Min.')} ${numberWithCurrency(minimumAmount, currency, {
          locale: currentUser?.locale || I18n.locale,
          currencyDisplay: 'narrowSymbol',
        })}`
      : s__('Donations|Enter amount');

  const minimum = minimumAmount > 0 ? minimumAmount : 0;

  return (
    <Box flexDirection="column">
      <Box font="h4-semibold">{title}</Box>
      {description && <Box mt={2}>{description}</Box>}
      <Box mt={3} alignItems="flex-start">
        <NumberInput
          placeholder={placeholder}
          name={name}
          validate={{
            customValidator: (value) => {
              if (Number(value) === 0) {
                return;
              }

              return validateMinimum(value, minimum);
            },
          }}
          wrapperProps={{ mb: 0 }}
        />
      </Box>
    </Box>
  );
};

const DonationModal = ({
  opened = false,
  donations = [],
  onClose = () => {},
  onSubmit,
  header,
}) => {
  const [isOpen, setOpen] = useState(opened);
  const { currentUser } = useDefaultAppContexts();

  useEffect(() => {
    setOpen(opened);
  }, [opened]);

  const initialValues = {
    donations: donations.map(({ amount = '', ...donation }) => ({
      ...donation,
      amount: donation?.cartItem?.total || amount,
    })),
  };

  if (!isOpen || donations.length === 0) {
    return null;
  }

  const validate = ({ donations }) => {
    const errors = {};

    donations.forEach((donation, i) => {
      const isInvalid =
        donation.amount !== 0 &&
        validateMinimum(donation.amount, donation.minimum_amount);

      if (isInvalid) {
        errors[i] = isInvalid;
      }
    });

    return errors;
  };

  return (
    <ModalWithTemplate
      onCloseModal={() => {
        setOpen(false);

        onClose();
      }}
      header={<Header header={header} />}
      width="medium"
      screenReadersCloseButtonText={s__(
        'NetworkEventsAccessibility|Close add a donation onto your order window',
      )}
      dataTestId="donation-modal"
    >
      <Formik
        initialValues={initialValues}
        validate={validate}
        onSubmit={async (values) => {
          await onSubmit(values);
        }}
      >
        {() => (
          <StyledForm>
            <ErrorFocus />
            {donations.map((donation, index) => (
              <Fragment key={donation.id}>
                <Box display="block">
                  <Donation
                    currentUser={currentUser}
                    name={`donations[${index}][amount]`}
                    {...donation}
                  />
                </Box>
                {index < donations.length - 1 && <Hr />}
              </Fragment>
            ))}
            <Footer onClose={onClose} />
          </StyledForm>
        )}
      </Formik>
    </ModalWithTemplate>
  );
};

DonationModal.propTypes = {
  donations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      title: PropTypes.string.isRequired,
      description: PropTypes.string,
      minimum_amount: PropTypes.number,
    }),
  ),
  opened: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
};

export default DonationModal;

const StyledForm = styled.form`
  width: 100%;
`;

const Hr = styled.hr`
  margin: ${({ theme }) => theme.space[5]}px 0;
  border-color: ${({ theme }) => theme.colors.horizon};
`;
